import React, { Component } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { ItemSummary } from '@/types/item/ItemSummary';

import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId, getBidderIsSuspended } from '@/redux/modules/account/user/user.selectors';
import { getCatalog } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { getItemSummaries } from '@/redux/modules/item/summary/itemSummary.selectors';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import {
    fetchReviewComments,
    fetchReviewData,
    getReviewComments,
    getReviewData,
    getReviewError,
    getReviewSubmitSuccess,
    getReviewSubmitted,
    resetReviewPage,
    ReviewSlice as ReviewState,
    submitReview,
} from '@/redux/modules/review';
import { getSeller } from '@/redux/modules/seller';
import { Catalog } from '@/types/Catalog';
import { Seller } from '@/types/Seller';

import { ReviewedPage } from './ReviewedPage/ReviewedPage';
import { ReviewPage } from './ReviewPage/ReviewPage';

const maxNoteSize = 2000;
const maxReviewSize = 2000;

type ReviewPageContainerProps = {
    baseUrl: string;
    bidderId: number;
    bidderIsSuspended: boolean;
    catalog: Catalog;
    completedReview: any;
    fetchItemSummariesIfNeeded: (params: Parameters<typeof fetchItemSummariesIfNeeded>[0]) => void;
    fetchReviewComments: (reviewId: number) => void;
    fetchReviewData: (catalogIds: number[]) => void;
    itemIds: number[];
    items: ItemSummary[];
    navigate: ReturnType<typeof useNavigate>;
    openSendSellerMessageModal: (
        messageDetails: any,
        sellerId: number,
        sellerName: string,
        subject: string,
        title: string
    ) => void;
    preventReview: boolean;
    reviewComments: any;
    reviewError: boolean;
    reviewSubmitted: boolean;
    reviewSubmittedSuccess: boolean;
    reviewed: boolean;
    seller: Seller;
    submitReview: (params: Parameters<typeof submitReview>[0]) => void;
};

type State = {
    accuracy: number;
    note: string;
    overall: number;
    payment: number;
    receivedItems: boolean;
    responsiveness: number;
    review: string;
    reviewHeadline: string;
    reviewHeadlineError: boolean;
    selectedContinue: boolean;
    selectedRecievedItems: boolean;
    shipping: number;
    talkedToHouse: boolean;
};

class ReviewPageContainer extends Component<ReviewPageContainerProps, State> {
    state = {
        accuracy: 0,
        note: '',
        overall: 0,
        payment: 0,
        receivedItems: false,
        responsiveness: -1,
        review: '',
        reviewHeadline: '',
        reviewHeadlineError: false,
        selectedContinue: false,
        selectedRecievedItems: false,
        shipping: 0,
        talkedToHouse: false,
    };

    componentDidUpdate(prevProps: any) {
        if (
            this.props.bidderIsSuspended ||
            (prevProps.preventReview !== this.props.preventReview && this.props.preventReview) ||
            ((prevProps.reviewSubmittedSuccess !== this.props.reviewSubmittedSuccess ||
                prevProps.reviewed !== this.props.reviewed) &&
                this.props.reviewSubmittedSuccess)
        ) {
            this.props.navigate('/dashboard/won-items');
        }
        if (prevProps.bidderId !== this.props.bidderId) {
            if (!this.props.catalog.catalogId) {
                this.props.navigate('/dashboard/won-items');
            } else {
                this.props.fetchReviewData([this.props.catalog.catalogId]);
            }
        }
        if (prevProps.itemIds && this.props.itemIds && prevProps.itemIds.length !== this.props.itemIds.length) {
            this.props.fetchItemSummariesIfNeeded({
                identifier: 'REVIEWS-PAGE-CONTAINER',
                itemIds: this.props.itemIds,
            });
        }
        if (this.props.items.length === 0) {
            this.props.fetchReviewData([this.props.catalog.catalogId]);
        }
        if (
            Boolean(this.props.completedReview) &&
            this.props.completedReview.reviewId &&
            (!prevProps.completedReview || prevProps.completedReview.reviewId !== this.props.completedReview.reviewId)
        ) {
            this.props.fetchReviewComments(this.props.completedReview.reviewId);
        }
        if (!this.state.reviewHeadlineError) {
            if (this.state.reviewHeadline.length > 100) {
                this.setState({ reviewHeadlineError: true });
            }
        } else {
            if (this.state.reviewHeadline.length <= 100) {
                this.setState({ reviewHeadlineError: false });
            }
        }
    }

    handleNotReceivedItemsClick = () =>
        this.setState({
            receivedItems: false,
            selectedContinue: false,
            selectedRecievedItems: true,
        });
    handleReceivedItemsClick = () => this.setState({ receivedItems: true, selectedRecievedItems: true });
    handleTalkedToHouseClick = () => this.setState({ responsiveness: -1, talkedToHouse: true });
    handleNotTalkedToHouseClick = () => this.setState({ talkedToHouse: false });
    handleContinueClick = () => this.setState({ selectedContinue: true });

    noteChange = ({ target }) => this.setState({ note: target.value });
    reviewChange = ({ target }) => this.setState({ review: target.value });
    reviewHeadlineChange = ({ target }) => this.setState({ reviewHeadline: target.value });
    ratingChangeAccuracy = (value: number) => this.setState({ accuracy: value });
    ratingChangeResponsiveness = (value: number) => this.setState({ responsiveness: value });
    ratingChangePayment = (value: number) => this.setState({ payment: value });
    ratingChangeShipping = (value: number) => this.setState({ shipping: value });
    ratingChangeOverall = (value: number) => this.setState({ overall: value });

    submitReview = () => {
        const { accuracy, note, overall, payment, responsiveness, review, reviewHeadline, shipping, talkedToHouse } =
            this.state;
        const { bidderId, catalog, seller } = this.props;
        const { catalogId } = catalog;
        const { sellerId } = seller;

        this.props.submitReview({
            accuracy,
            bidderId,
            catalogId,
            note,
            overall,
            payment,
            responsiveness: talkedToHouse ? responsiveness : -1,
            review,
            reviewHeadline,
            sellerId,
            shipping,
        });
    };

    render() {
        const {
            baseUrl,
            catalog,
            completedReview,
            items,
            openSendSellerMessageModal,
            reviewComments,
            reviewed,
            reviewError,
            reviewSubmitted,
            seller,
        } = this.props;

        const {
            accuracy,
            note,
            overall,
            payment,
            receivedItems,
            responsiveness,
            review,
            reviewHeadline,
            reviewHeadlineError,
            selectedContinue,
            selectedRecievedItems,
            shipping,
            talkedToHouse,
        } = this.state;

        const reviewValid = review.length === 0 || review.length <= maxReviewSize;
        const noteValid = note.length === 0 || note.length <= maxNoteSize;
        const disableSubmit =
            !accuracy ||
            !overall ||
            !payment ||
            !shipping ||
            reviewSubmitted ||
            reviewError ||
            !noteValid ||
            !reviewValid ||
            (talkedToHouse && responsiveness === -1) ||
            Boolean(reviewHeadlineError) ||
            (Boolean(review) && !reviewHeadline) ||
            (Boolean(reviewHeadline) && !review);

        return (
            <EnsureLoggedInContainer>
                {reviewed ? (
                    <ReviewedPage
                        baseUrl={baseUrl}
                        catalog={catalog}
                        completedReview={completedReview}
                        items={items}
                        reviewComments={reviewComments}
                        seller={seller}
                    />
                ) : (
                    <ReviewPage
                        baseUrl={baseUrl}
                        catalog={catalog}
                        disableSubmit={disableSubmit}
                        handleContinueClick={this.handleContinueClick}
                        handleNotReceivedItemsClicked={this.handleNotReceivedItemsClick}
                        handleNotTalkedToHouseClick={this.handleNotTalkedToHouseClick}
                        handleReceivedItemsClicked={this.handleReceivedItemsClick}
                        handleTalkedToHouseClick={this.handleTalkedToHouseClick}
                        items={items}
                        note={note}
                        noteChange={this.noteChange}
                        noteValid={noteValid}
                        openSendSellerMessageModal={openSendSellerMessageModal}
                        ratingChangeAccuracy={this.ratingChangeAccuracy}
                        ratingChangeOverall={this.ratingChangeOverall}
                        ratingChangePayment={this.ratingChangePayment}
                        ratingChangeResponsiveness={this.ratingChangeResponsiveness}
                        ratingChangeShipping={this.ratingChangeShipping}
                        receivedItems={receivedItems}
                        review={review}
                        reviewChange={this.reviewChange}
                        reviewError={reviewError}
                        reviewHeadlineChange={this.reviewHeadlineChange}
                        reviewHeadlineError={reviewHeadlineError}
                        reviewSubmitted={reviewSubmitted}
                        reviewValid={reviewValid}
                        selectedContinue={selectedContinue}
                        selectedRecievedItems={selectedRecievedItems}
                        submitReview={this.submitReview}
                        talkedToHouse={talkedToHouse}
                    />
                )}
            </EnsureLoggedInContainer>
        );
    }
}

type ReviewPageParams = {
    catalogId: string;
};

/**
 * This function allowed removing react-redux connect and redux-first-history from the class above without rewriting its componentDidUpdate craziness
 */
const ReviewPageContainerWrapper = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const params = useParams<ReviewPageParams>();
    const catalogId = Number(params.catalogId);

    const baseUrl = useAppSelector(getBaseUrl);
    const catalog = useAppSelector((state) => getCatalog(state, catalogId));
    const seller = useAppSelector((state) => getSeller(state, catalog.sellerId));
    const bidderId = useAppSelector(getBidderId);
    const bidderIsSuspended = useAppSelector(getBidderIsSuspended);
    const reviewError = useAppSelector(getReviewError);
    const reviewSubmitted = useAppSelector(getReviewSubmitted);
    const reviewSubmittedSuccess = useAppSelector(getReviewSubmitSuccess);
    const {
        lots: itemIds,
        preventReview,
        review: completedReview,
        reviewed,
    } = useAppSelector(getReviewData) as Extract<ReviewState['reviewData'], { lots: any }>;
    const reviewComments = useAppSelector(getReviewComments);
    const items = useAppSelector((state) => getItemSummaries(state, itemIds));

    React.useEffect(() => {
        dispatch(resetReviewPage());
    }, [dispatch, location]);

    return (
        <ReviewPageContainer
            baseUrl={baseUrl}
            bidderId={bidderId}
            bidderIsSuspended={bidderIsSuspended}
            catalog={catalog}
            completedReview={completedReview}
            fetchItemSummariesIfNeeded={(params: Parameters<typeof fetchItemSummariesIfNeeded>[0]) =>
                dispatch(fetchItemSummariesIfNeeded(params))
            }
            fetchReviewComments={(reviewId: number) => dispatch(fetchReviewComments(reviewId))}
            fetchReviewData={(catalogIds: number[]) => dispatch(fetchReviewData(catalogIds))}
            itemIds={itemIds}
            items={items}
            navigate={navigate}
            openSendSellerMessageModal={(
                messageDetails: any,
                sellerId: number,
                sellerName: string,
                subject: string,
                title: string
            ) => dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject, title))}
            preventReview={preventReview}
            reviewComments={reviewComments}
            reviewed={reviewed}
            reviewError={reviewError}
            reviewSubmitted={reviewSubmitted}
            reviewSubmittedSuccess={reviewSubmittedSuccess}
            seller={seller}
            submitReview={(params: Parameters<typeof submitReview>[0]) => dispatch(submitReview(params))}
        />
    );
};

export default ReviewPageContainerWrapper;
